import { ChangeEvent, useMemo } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { noop } from 'lodash-es';
import { theme } from '@frontend/theme';
import {
  CheckboxField,
  CustomChangeEvent,
  DatePickerField,
  DropdownField,
  EmailField,
  FieldChangeEvent,
  PhoneField,
  PostalCodeField,
  Text,
  TextField,
} from '@frontend/design-system';
import { useConfigShallowStore } from '../../../../../stores';
import { ButtonAlignment, ButtonType, Category } from '../../../../../types/form-template.types';
import { getFieldOptions } from '../helpers';
import { useFieldContext } from '../hooks/use-app-form';
import { useDynamicFormStore } from '../store';
import { SchemaFormFieldType } from '../types';
import { ButtonField } from './button-field';
import { CategorizedChecklistField } from './categorized-checklist-field';
import { ChecklistField } from './checklist-field';
import { OptionSwitchField } from './optionswitch-field';
import { RadioField } from './radio-field';
import { TextWithButtonField } from './text-with-button-field';
import { TextareaField } from './textarea-field';

export interface FormFieldProps {
  fieldKey: string;
  label: string;
  type: SchemaFormFieldType;
  options?: string | Array<{ label: string; value: string }>;
  categories?: Category[];
  fieldMeta?: Record<string, unknown>;
  /** Keep the field's built-in inline/floating label instead of rendering a dedicated label above it. */
  keepInlineLabel?: boolean;
}

const dedicatedLabelStyle = css`
  margin-bottom: ${theme.spacing(0.5)};
  display: inline-block;
`;

// CheckboxField only outlines an error; it never renders the message.
const checkboxErrorStyle = css`
  display: block;
  margin-top: ${theme.spacing(0.5)};
  color: ${theme.colors.text.critical};
`;

const DedicatedLabel = ({ htmlFor, children }: { htmlFor: string; children: string }) => (
  <label htmlFor={htmlFor} css={dedicatedLabelStyle}>
    <Text as='span'>{children}</Text>
  </label>
);

export const FormField = ({
  fieldKey,
  label,
  type,
  options,
  categories,
  fieldMeta,
  keepInlineLabel = false,
}: FormFieldProps) => {
  const field = useFieldContext<unknown>();

  const { formFieldsMap, conditionsMap, executeFieldUpdates } = useDynamicFormStore([
    'formFieldsMap',
    'conditionsMap',
    'executeFieldUpdates',
  ]);
  const { postalCodeLocale } = useConfigShallowStore('postalCodeLocale');

  // Check if this field has update actions in its conditions
  const hasUpdateActions = useMemo(() => {
    const currentField = formFieldsMap[fieldKey];
    if (!currentField?.sourceConditionIds?.length) return false;

    return currentField.sourceConditionIds.some((conditionId) => {
      const condition = conditionsMap[conditionId];
      return condition?.actions.some((action) => action.operation === 'update' && action.type === 'fields');
    });
  }, [formFieldsMap, fieldKey, conditionsMap]);

  const hasError = field.state.meta.isTouched && field.state.meta.errors?.length > 0;
  const errorMessage = hasError ? field.state.meta.errors.join(', ') : '';

  const inlineLabel = keepInlineLabel ? label : '';

  const applyFieldUpdates = () => {
    const updates = executeFieldUpdates(fieldKey);

    // Apply all updates to TanStack form
    Object.entries(updates).forEach(([key, value]) => {
      field.form.setFieldValue(key, value);
    });
  };

  const handleChange = (e?: FieldChangeEvent) => {
    if (!e) return;

    let newValue: unknown;
    if ('target' in e) {
      newValue = (e as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>).target.value;
    } else {
      newValue = (e as CustomChangeEvent).value;
    }

    // Update TanStack form state
    field.handleChange(newValue);

    // If this field has update actions, execute them and apply to TanStack form
    if (hasUpdateActions) {
      applyFieldUpdates();
    }
  };

  const renderComponent = () => {
    switch (type) {
      case 'date':
        return (
          <>
            {!keepInlineLabel && <DedicatedLabel htmlFor={field.name}>{label}</DedicatedLabel>}
            <DatePickerField
              css={css`
                max-width: unset;
              `}
              id={field.name}
              name={field.name}
              label={inlineLabel}
              active={false}
              aria-invalid={hasError}
              touched={field.state.meta.isTouched}
              error={errorMessage}
              onBlur={field.handleBlur}
              onChange={handleChange}
              onFocus={noop}
              value={field.state.value as string}
              maxDate={field.name === 'birthdate' ? dayjs().format('MM/DD/YYYY') : undefined}
            />
          </>
        );

      case 'email':
        return (
          <>
            {!keepInlineLabel && <DedicatedLabel htmlFor={field.name}>{label}</DedicatedLabel>}
            <EmailField
              id={field.name}
              name={field.name}
              label={inlineLabel}
              active={false} // TODO: implement active state if needed (determined by focus of field)
              aria-invalid={hasError}
              autoComplete='off'
              touched={field.state.meta.isTouched}
              error={errorMessage}
              onBlur={field.handleBlur}
              onChange={handleChange}
              onFocus={noop}
              value={field.state.value as string}
            />
          </>
        );

      case 'phone':
        return (
          <>
            {!keepInlineLabel && <DedicatedLabel htmlFor={field.name}>{label}</DedicatedLabel>}
            <PhoneField
              id={field.name}
              name={field.name}
              label={inlineLabel}
              active={false}
              aria-invalid={hasError}
              touched={field.state.meta.isTouched}
              error={errorMessage}
              onBlur={field.handleBlur}
              onChange={handleChange}
              onFocus={noop}
              value={field.state.value as string}
              format='standard'
            />
          </>
        );

      case 'postalCode':
        return (
          <>
            {!keepInlineLabel && <DedicatedLabel htmlFor={field.name}>{label}</DedicatedLabel>}
            <PostalCodeField
              id={field.name}
              name={field.name}
              label={inlineLabel}
              active={false}
              aria-invalid={hasError}
              touched={field.state.meta.isTouched}
              error={errorMessage}
              onBlur={field.handleBlur}
              onChange={handleChange}
              onFocus={noop}
              value={field.state.value as string}
              locale={postalCodeLocale}
            />
          </>
        );

      case 'select': {
        const selectOptions = getFieldOptions(options);

        return (
          <>
            {!keepInlineLabel && <DedicatedLabel htmlFor={field.name}>{label}</DedicatedLabel>}
            <DropdownField
              id={field.name}
              name={field.name}
              label={inlineLabel}
              active={false}
              aria-invalid={hasError}
              touched={field.state.meta.isTouched}
              error={errorMessage}
              onBlur={field.handleBlur}
              onChange={handleChange}
              onFocus={noop}
              value={field.state.value as string}
            >
              {selectOptions.map((option) => (
                <DropdownField.Option key={option.value} value={option.value} searchValue={option.value}>
                  {option.label}
                </DropdownField.Option>
              ))}
            </DropdownField>
          </>
        );
      }

      case 'checkbox':
        return (
          <>
            <CheckboxField
              id={field.name}
              name={field.name}
              label={label}
              active={false}
              aria-invalid={hasError}
              touched={field.state.meta.isTouched}
              error={errorMessage}
              onBlur={field.handleBlur}
              onChange={handleChange}
              onFocus={noop}
              value={field.state.value as boolean}
            />
            {hasError && (
              <Text size='small' css={checkboxErrorStyle}>
                {errorMessage}
              </Text>
            )}
          </>
        );

      case 'signature':
        return <Text>Signature Field</Text>;

      case 'textarea':
        return (
          <>
            {!keepInlineLabel && <DedicatedLabel htmlFor={field.name}>{label}</DedicatedLabel>}
            <TextareaField
              id={field.name}
              name={field.name}
              label={inlineLabel}
              active={false}
              aria-invalid={hasError}
              touched={field.state.meta.isTouched}
              error={errorMessage}
              onBlur={field.handleBlur}
              onChange={handleChange}
              onFocus={noop}
              value={field.state.value as string}
            />
          </>
        );

      case 'multiselect-dropdown':
      case 'checklist': {
        const checklistOptions = getFieldOptions(options);
        return (
          <ChecklistField
            id={field.name}
            name={field.name}
            label={label}
            aria-invalid={hasError}
            touched={field.state.meta.isTouched}
            error={errorMessage}
            onBlur={field.handleBlur}
            onChange={handleChange}
            value={field.state.value as string[]}
            options={checklistOptions}
          />
        );
      }

      case 'radio': {
        const radioOptions = getFieldOptions(options);
        return (
          <RadioField
            id={field.name}
            name={field.name}
            label={label}
            aria-invalid={hasError}
            touched={field.state.meta.isTouched}
            error={errorMessage}
            onBlur={field.handleBlur}
            onChange={handleChange}
            value={field.state.value as string}
            options={radioOptions}
          />
        );
      }

      case 'categorizedChecklist':
        return (
          <CategorizedChecklistField
            id={field.name}
            name={field.name}
            label={label}
            aria-invalid={hasError}
            touched={field.state.meta.isTouched}
            error={errorMessage}
            onBlur={field.handleBlur}
            onChange={handleChange}
            value={field.state.value as string[]}
            categories={categories ?? []}
          />
        );

      case 'searchable-listbox':
        return <Text>Searchable Listbox Field</Text>;

      case 'optionswitch': {
        const optionSwitchOptions = getFieldOptions(options);
        return (
          <OptionSwitchField
            id={field.name}
            name={field.name}
            label={label}
            aria-invalid={hasError}
            touched={field.state.meta.isTouched}
            error={errorMessage}
            onBlur={field.handleBlur}
            onChange={handleChange}
            value={field.state.value as string}
            options={optionSwitchOptions}
          />
        );
      }

      case 'button': {
        const handleButtonClick = () => {
          applyFieldUpdates();
        };

        const buttonType = fieldMeta?.buttonType as ButtonType;
        const buttonAlign = fieldMeta?.align as ButtonAlignment;

        return (
          <ButtonField
            id={field.name}
            label={label}
            fieldKey={fieldKey}
            buttonType={buttonType}
            align={buttonAlign}
            onClick={handleButtonClick}
          />
        );
      }

      case 'text-with-button': {
        const buttonLabel = fieldMeta?.buttonLabel as string | undefined;
        const buttonType = fieldMeta?.buttonType as ButtonType | undefined;

        // Guard clause: ensure fieldMeta has required button properties
        if (!buttonLabel || !buttonType) {
          return null;
        }

        const handleButtonClick = () => {
          applyFieldUpdates();
        };

        return (
          <TextWithButtonField
            label={label}
            fieldKey={fieldKey}
            buttonLabel={buttonLabel}
            buttonType={buttonType}
            onButtonClick={handleButtonClick}
          />
        );
      }

      case 'currentDatePopulator':
        return (
          <>
            {!keepInlineLabel && <DedicatedLabel htmlFor={field.name}>{label}</DedicatedLabel>}
            <DatePickerField
              id={field.name}
              name={field.name}
              label={inlineLabel}
              active={false}
              aria-invalid={hasError}
              touched={field.state.meta.isTouched}
              error={errorMessage}
              onBlur={noop}
              onChange={noop}
              onFocus={noop}
              value={field.state.value as string}
            />
          </>
        );

      default:
        return (
          <>
            {!keepInlineLabel && <DedicatedLabel htmlFor={field.name}>{label}</DedicatedLabel>}
            <TextField
              id={field.name}
              name={field.name}
              label={inlineLabel}
              active={false} // TODO: implement active state if needed (determined by focus of field)
              aria-invalid={hasError}
              autoComplete='off'
              touched={field.state.meta.isTouched}
              error={errorMessage}
              onBlur={field.handleBlur}
              onChange={handleChange}
              onFocus={noop}
              value={String(field.state.value ?? '')}
            />
          </>
        );
    }
  };

  // data-invalid lets the submit handler find this field even when the rendered control doesn't expose aria-invalid (radio, optionswitch, checklists)
  return <div data-invalid={hasError || undefined}>{renderComponent()}</div>;
};
